import React from "react";
import style from "../../components/EmployeeUserList/EmployeeUserList.module.css";
import dayjs from "dayjs";
import { formatDOB } from "../../utils/dateUtils";

// ✅ Used for displaying days of the week in Russian
const weekdayMap = {
  Monday: "Пн",
  Tuesday: "Вт",
  Wednesday: "Ср",
  Thursday: "Чт",
  Friday: "Пт",
  Saturday: "Сб",
  Sunday: "Вс",
};

const weekdayOrder = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

const sorterTooltipRu = {
  title: (direction, column) => {
    if (!direction) return "Нажмите для сортировки";
    if (direction === "ascend") return "Сортировать по возрастанию";
    if (direction === "descend") return "Сортировать по убыванию";
    return "";
  },
};

// 🔁 Reusable: clickable surname
const clickableSurnameColumn = (openModal) => ({
  title: "Фамилия",
  dataIndex: "user_surname",
  key: "user_surname",
  render: (_, row) => (
    <span
      onClick={() => openModal(row)}
      style={{
        color: "#1890ff",
        cursor: "pointer",
        textDecoration: "underline",
      }}
    >
      {row.user_surname}
    </span>
  ),
});

// 🔁 Reusable: formatted DOB
const formattedDobColumn = {
  title: "Дата рождения",
  dataIndex: "date_of_birth",
  key: "date_of_birth",
  render: (date) => {
    const parsed = dayjs(date, ["D.M.YYYY", "DD.MM.YYYY", "YYYY-MM-DD"], true);
    return parsed.isValid() ? parsed.format("DD.MM.YYYY") : "—";
  },
};

//
// =============== AZAG ====================
//

const AzagCardBody = (user) => (
  <>
    <div>
      <div className={style.card_label}>Гос. номер машины: </div>
      <div className={style.card_value}>{user.license_plate || "—"}</div>
    </div>
    <div>
      <div className={style.card_label}>Тип сотрудника:</div>
      <div className={style.card_value}>{user.emp_type || "—"}</div>
    </div>
    <div>
      <div className={style.card_label}>Статус:</div>
      <div className={style.card_value}>{user.emp_category || "—"}</div>
    </div>
    <div>
      <div className={style.card_label}>Дата рождения:</div>
      <div className={style.card_value}>{formatDOB(user.date_of_birth)}</div>
    </div>
  </>
);

const AzagColumns = (openModal) => [
  {
    title: "№",
    dataIndex: "index",
    key: "index",
    render: (_, __, index) => index + 1,
    width: 50,
  },
  clickableSurnameColumn(openModal),
  {
    title: "Имя",
    dataIndex: "user_name",
    key: "user_name",
  },
  {
    title: "Должность",
    dataIndex: "emp_position",
    key: "emp_position",
  },
  {
    title: "Гос. номер машины",
    dataIndex: "license_plate",
    key: "license_plate",
  },
  {
    title: "Тип сотрудника",
    dataIndex: "emp_type",
    key: "emp_type",
  },
  {
    title: "Статус",
    dataIndex: "emp_category",
    key: "emp_category",
  },

  formattedDobColumn,
];

//
// =============== GALEREYA ====================
//

const GalereyaCardBody = (user) => (
  <>
    <div>
      <div className={style.card_label}>Офис:</div>
      <div className={style.card_value}>{user.emp_office || "Не указано"}</div>
    </div>
    <div>
      <div className={style.card_label}>Подразделение:</div>
      <div className={style.card_value}>{user.emp_unit || "Не указано"}</div>
    </div>
    <div>
      <div className={style.card_label}>Тип работы:</div>
      <div className={style.card_value}>{user.work_type || "Не указано"}</div>
    </div>
    <div>
      <div className={style.card_label}>Рабочие дни:</div>
      <div className={style.card_value}>
        {Array.isArray(user.work_days)
          ? weekdayOrder
              .filter((day) =>
                user.work_days.map((d) => weekdayMap[d] || d).includes(day)
              )
              .join(", ")
          : "Не указано"}
      </div>
    </div>
    <div className={style.card_footer}>
      <div>
        <b>Рабочий день:</b> {user.start_work_time || "-"} -{" "}
        {user.end_work_time || "-"}
      </div>
    </div>
  </>
);

const GalereyaColumns = (openModal) => [
  {
    title: "№",
    dataIndex: "index",
    key: "index",
    render: (_, __, index) => index + 1,
    width: 50,
  },
  {
    title: "Фамилия",
    dataIndex: "user_surname",
    key: "user_surname",
    render: (_, row) => (
      <span
        onClick={() => openModal(row)}
        style={{
          color: "#1890ff",
          cursor: "pointer",
          textDecoration: "underline",
        }}
      >
        {row.user_surname}
      </span>
    ),
    sorter: (a, b) => a.user_surname.localeCompare(b.user_surname, "ru"),
    showSorterTooltip: sorterTooltipRu,
  },
  {
    title: "Имя",
    dataIndex: "user_name",
    key: "user_name",
    sorter: (a, b) => a.user_name.localeCompare(b.user_name, "ru"),
    showSorterTooltip: sorterTooltipRu,
  },
  {
    title: "Офис",
    dataIndex: "emp_office",
    key: "emp_office",
    sorter: (a, b) => a.emp_office?.localeCompare(b.emp_office, "ru"),
    showSorterTooltip: sorterTooltipRu,
  },
  {
    title: "Подразделение",
    dataIndex: "emp_unit",
    key: "emp_unit",
    sorter: (a, b) => a.emp_unit?.localeCompare(b.emp_unit, "ru"),
    showSorterTooltip: sorterTooltipRu,
  },
  {
    title: "Тип работы",
    dataIndex: "work_type",
    key: "work_type",
    sorter: (a, b) => a.work_type?.localeCompare(b.work_type, "ru"),
    showSorterTooltip: sorterTooltipRu,
  },
  {
    title: "Рабочие дни",
    dataIndex: "work_days",
    key: "work_days",
    render: (days) => {
      if (!Array.isArray(days)) return "Не указано";
      const ruDays = days.map((d) => weekdayMap[d] || d);
      return weekdayOrder.filter((day) => ruDays.includes(day)).join(", ");
    },
    sorter: (a, b) => {
      const getRuDayIndexesSum = (days) => {
        if (!Array.isArray(days)) return 999;
        const ruDays = days.map((d) => weekdayMap[d] || d);
        return ruDays.reduce(
          (sum, day) =>
            sum +
            (weekdayOrder.includes(day) ? weekdayOrder.indexOf(day) : 100),
          0
        );
      };
      return getRuDayIndexesSum(a.work_days) - getRuDayIndexesSum(b.work_days);
    },
    showSorterTooltip: sorterTooltipRu,
  },
  {
    title: "Должность",
    dataIndex: "emp_position",
    key: "emp_position",
    sorter: (a, b) => a.emp_position?.localeCompare(b.emp_position, "ru"),
    showSorterTooltip: sorterTooltipRu,
  },
  {
    title: "Начало дня",
    dataIndex: "start_work_time",
    key: "start_work_time",
    sorter: (a, b) =>
      (a.start_work_time || "").localeCompare(b.start_work_time || ""),
    showSorterTooltip: sorterTooltipRu,
  },
  {
    title: "Конец дня",
    dataIndex: "end_work_time",
    key: "end_work_time",
    sorter: (a, b) =>
      (a.end_work_time || "").localeCompare(b.end_work_time || ""),
    showSorterTooltip: sorterTooltipRu,
  },
];

//
// =============== EXPORT ====================
//

const CLIENT_CONFIG = {
  Azag: {
    renderCardBody: AzagCardBody,
    renderTableColumns: AzagColumns,
  },
  Galereya: {
    renderCardBody: GalereyaCardBody,
    renderTableColumns: GalereyaColumns,
  },
};

export default CLIENT_CONFIG;
